/**
 * Helper funkce pro práci s výrobním dnem.
 * Výrobní den nezačíná v 00:00, ale v čase definovaném v TenantModel.dayStart.
 * Například při dayStart = "06:00:00":
 *   - Kalendářní den 7.10.2025 00:00 - 8.10.2025 00:00
 *   - Výrobní den 7.10.2025 06:00 - 8.10.2025 06:00
 */

import { tenantsStore } from '@/libs/tenants/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { TenantModel } from '@/libs/Api';
import moment from 'moment-timezone';

// Timezone pro zobrazování a výpočty - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';

/**
 * Získá čas zahájení výrobního dne v minutách od půlnoci.
 * Pokud dayStart není definován, vrací 0 (půlnoc).
 * 
 * Příklad: dayStart = "06:00:00" → vrací 360 minut
 * 
 * @returns Počet minut od půlnoci, kdy začíná výrobní den (0 = půlnoc, standardní kalendářní den)
 */
export function getDayStartMinutes(): number {
    try {
        // Načteme tenant model ze store
        const tenant: TenantModel = tenantsStore.useGetter(CrudGetter.Detail);
        const dayStart = tenant?.dayStart;
        
        // Pokud není definován, používáme standardní půlnoc
        if (!dayStart || typeof dayStart !== 'string') {
            return 0;
        }

        // Parse formátu HH:mm:ss nebo HH:mm (např. "06:00:00" nebo "06:00")
        const parts = dayStart.split(':');
        if (parts.length < 2) {
            return 0;
        }

        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);

        if (isNaN(hours) || isNaN(minutes)) {
            return 0;
        }

        // Převedeme na celkový počet minut od půlnoci
        return hours * 60 + minutes;
    } catch {
        // V případě chyby (např. store není inicializován) vracíme 0
        return 0;
    }
}

/**
 * Vrací timestamp začátku aktuálního BĚŽÍCÍHO výrobního dne pro daný timestamp.
 * 
 * Příklad 1: při dayStart = "06:00:00" a vstupním času 14.10.2025 14:30:00
 *   - Aktuální výrobní den začal: 14.10.2025 06:00:00
 *   - Vrátí: 14.10.2025 06:00:00
 * 
 * Příklad 2: při dayStart = "06:00:00" a vstupním času 14.10.2025 03:00:00
 *   - Aktuální čas JE PŘED dayStart → stále běží VČEREJŠÍ výrobní den!
 *   - Aktuální výrobní den začal: 13.10.2025 06:00:00
 *   - Vrátí: 13.10.2025 06:00:00
 * 
 * @param ms Timestamp (v ms)
 * @returns Timestamp začátku aktuálního běžícího výrobního dne (v ms)
 */
export function startOfProductionDayMs(ms: number): number {
    const dayStartMinutes = getDayStartMinutes();
    const dayStartHours = Math.floor(dayStartMinutes / 60);
    const dayStartMins = dayStartMinutes % 60;
    
    // Vytvoříme moment v Prague timezone
    const currentTime = moment.tz(ms, DISPLAY_TIMEZONE);
    
    // Začátek výrobního dne = půlnoc + dayStart offset
    const currentProdDayStart = currentTime.clone()
        .startOf('day')
        .hour(dayStartHours)
        .minute(dayStartMins)
        .second(0)
        .millisecond(0);
    
    // KLÍČOVÁ LOGIKA: Pokud je aktuální čas PŘED dayStart, stále běží VČEREJŠÍ výrobní den!
    // Příklad: 14.10.2025 03:00, dayStart 06:00 → stále běží výrobní den 13.10.2025 06:00 - 14.10.2025 06:00
    /*if (currentTime.isBefore(currentProdDayStart)) {
        return currentProdDayStart.subtract(1, 'day').valueOf();
    }*/
    
    return currentProdDayStart.valueOf();
}

/**
 * Vrací timestamp konce výrobního dne (začátek následujícího výrobního dne).
 * 
 * Příklad: při dayStart = "06:00:00" a vstupním času 7.10.2025 14:30:00
 *   - Výrobní den 7.10. končí: 8.10.2025 06:00:00
 *   - To je zároveň začátek výrobního dne 8.10.
 * 
 * Důležité: Tento čas je EXKLUZIVNÍ horní hranice výrobního dne.
 * Při dotazech na server používáme `lt` (less than), ne `lte` (less than or equal).
 * 
 * @param ms Timestamp v kalendářním dni (v ms)
 * @returns Timestamp konce výrobního dne = začátek následujícího výrobního dne (v ms)
 */
export function endOfProductionDayMs(ms: number): number {
    const dayStartMinutes = getDayStartMinutes();
    const dayStartHours = Math.floor(dayStartMinutes / 60);
    const dayStartMins = dayStartMinutes % 60;
    
    // Vytvoříme moment v Prague timezone
    const currentTime = moment.tz(ms, DISPLAY_TIMEZONE);
    
    // Začátek následujícího výrobního dne = půlnoc příštího dne + dayStart offset
    const nextProdDayStart = currentTime.clone()
        .startOf('day')
        .add(1, 'day')
        .hour(dayStartHours)
        .minute(dayStartMins)
        .second(0)
        .millisecond(0);
    
    return nextProdDayStart.valueOf();
}

/**
 * Vrací klíč dne (YYYY-MM-DD) pro daný timestamp podle výrobního dne.
 * Timestamp náleží do výrobního dne, který začal v daný kalendářní den.
 * 
 * Příklad: při dayStart = "06:00:00"
 *   - Čas 7.10.2025 14:30 → klíč "2025-10-07" (výrobní den 7.10.)
 *   - Čas 7.10.2025 03:00 → klíč "2025-10-06" (stále ještě výrobní den 6.10., který končí v 7.10. 06:00)
 * 
 * Logika: Od času odečteme dayStart offset, čímž posuneme čas "zpět" a získáme
 * kalendářní den, ve kterém tento výrobní den začal.
 * 
 * @param ms Timestamp (v ms)
 * @returns Klíč dne ve formátu YYYY-MM-DD
 */
export function productionDayKey(ms: number): string {
    const dayStartMinutes = getDayStartMinutes();
    
    // Vytvoříme moment v Prague timezone
    const currentTime = moment.tz(ms, DISPLAY_TIMEZONE);
    
    // Odečteme dayStart offset, abychom získali správný kalendářní den
    // Příklad: 7.10. 14:30 - 6h = 7.10. 08:30 → den 7.10.
    //          7.10. 03:00 - 6h = 6.10. 21:00 → den 6.10.
    const adjustedTime = currentTime.clone().subtract(dayStartMinutes, 'minutes');
    
    return adjustedTime.format('YYYY-MM-DD');
}

/**
 * Pomocná funkce pro padding čísel (01, 02, ..., 10, 11, ...)
 */
function pad2(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
}

/**
 * Vrací začátek kalendářního dne v 00:00 (pro zpětnou kompatibilitu).
 */
export function startOfCalendarDayMs(ms: number): number {
    return moment.tz(ms, DISPLAY_TIMEZONE).startOf('day').valueOf();
}

/**
 * Vrací konec kalendářního dne ve 23:59:59.999.
 */
export function endOfCalendarDayMs(ms: number): number {
    return moment.tz(ms, DISPLAY_TIMEZONE).endOf('day').valueOf();
}

/**
 * Konvertuje datum na začátek výrobního dne.
 * @param date Datum (může být Date, string nebo timestamp)
 * @returns ISO string začátku výrobního dne
 */
export function toProductionDayStart(date: Date | string | number): string {
    const ms = typeof date === 'number' ? date : moment.tz(date, DISPLAY_TIMEZONE).valueOf();
    return moment.tz(startOfProductionDayMs(ms), DISPLAY_TIMEZONE).toISOString();
}

/**
 * Konvertuje datum na konec výrobního dne.
 * Pokud je datum již přesně na začátku výrobního dne, vrací ho beze změny
 * (protože začátek výrobního dne = konec předchozího výrobního dne).
 * 
 * @param date Datum (může být Date, string nebo timestamp)
 * @returns ISO string konce výrobního dne
 */
export function toProductionDayEnd(date: Date | string | number): string {
    const ms = typeof date === 'number' ? date : moment.tz(date, DISPLAY_TIMEZONE).valueOf();
    const dayStartMinutes = getDayStartMinutes();
    const dayStartHours = Math.floor(dayStartMinutes / 60);
    const dayStartMins = dayStartMinutes % 60;
    
    const currentTime = moment.tz(ms, DISPLAY_TIMEZONE);
    
    // Zkontrolovat, zda je vstupní čas již přesně na začátku výrobního dne
    const currentDayStart = currentTime.clone()
        .startOf('day')
        .hour(dayStartHours)
        .minute(dayStartMins)
        .second(0)
        .millisecond(0);
        
    if (currentTime.valueOf() === currentDayStart.valueOf()) {
        // Už je na začátku výrobního dne (= konec předchozího dne), vrátit beze změny
        return currentTime.toISOString();
    }
    
    return moment.tz(endOfProductionDayMs(ms), DISPLAY_TIMEZONE).toISOString();
}

/**
 * Vrací popis výrobního dne pro uživatele.
 * @returns Textový popis (např. "Výrobní den začíná v 06:00")
 */
export function getProductionDayDescription(): string {
    const dayStartMinutes = getDayStartMinutes();
    const hours = Math.floor(dayStartMinutes / 60);
    const minutes = dayStartMinutes % 60;
    return `${pad2(hours)}:${pad2(minutes)}`;
}

/**
 * Zjišťuje, zda je výrobní den definován (dayStart != 00:00).
 */
export function hasCustomProductionDay(): boolean {
    return getDayStartMinutes() !== 0;
}

/**
 * Vrací konec posledního DOKONČENÉHO výrobního dne.
 * 
 * Pokud je např. 14.10.2025 13:54 a dayStart je 6:00:
 * - Aktuální výrobní den je 14.10. (začal 14.10. v 6:00, skončí 15.10. v 6:00)
 * - Protože aktuální výrobní den ještě neskončil, vrací 14.10.2025 6:00
 * 
 * Pokud je např. 14.10.2025 05:00 a dayStart je 6:00:
 * - Stále ještě běží výrobní den 13.10. (začal 13.10. v 6:00, skončí 14.10. v 6:00)
 * - Vrací 13.10.2025 6:00 (konec dne 12.10.)
 * 
 * @returns ISO string konce posledního dokončeného výrobního dne
 */
export function getLastCompletedProductionDayEnd(): string {
    // Začátek aktuálního výrobního dne je zároveň konec předchozího
    return moment.tz(startOfProductionDayMs(Date.now()), DISPLAY_TIMEZONE).toISOString();
}

/**
 * Vrací začátek výrobního dne jako ISO string s timezone offsetem.
 * 
 * Příklad: při dayStart = "06:00:00" a timezone = "Europe/Prague"
 *   - Vstup: timestamp pro 14.10.2025 14:30
 *   - Výstup: "2025-10-14T06:00:00+02:00" (letní čas)
 * 
 * DŮLEŽITÉ: 
 * - Vrací ISO string s explicitním timezone offsetem (např. +01:00, +02:00)
 * - Správně zpracovává DST přechody (letní/zimní čas)
 * - Vytváří moment s konkrétním datem a časem, což zajistí správný DST offset
 * 
 * @param date Datum (může být Date, string nebo timestamp)
 * @returns ISO string s timezone offsetem (např. "2025-10-27T06:00:00+01:00")
 */
export function startOfProductionDayIso(date: Date | string | number): string {
    const dayStartMinutes = getDayStartMinutes();
    const dayStartHours = Math.floor(dayStartMinutes / 60);
    const dayStartMins = dayStartMinutes % 60;
    
    // Vytvoříme moment v Prague timezone (VŽDY Praha, ne lokální TZ prohlížeče!)
    const currentTime = moment.tz(date, DISPLAY_TIMEZONE);
    
    // Vytvoříme moment pro začátek dne (půlnoc) a nastavíme čas
    const currentProdDayStart = currentTime.clone()
        .startOf('day')
        .hour(dayStartHours)
        .minute(dayStartMins)
        .second(0)
        .millisecond(0);
    
    // Pokud je aktuální čas PŘED dayStart, použijeme včerejší výrobní den
    /*if (currentTime.isBefore(currentProdDayStart)) {
        return currentProdDayStart.subtract(1, 'day').format();
    }*/
    
    return currentProdDayStart.format();
}

/**
 * Vrací konec výrobního dne jako ISO string s timezone offsetem.
 * 
 * Příklad: při dayStart = "06:00:00" a timezone = "Europe/Prague"
 *   - Vstup: timestamp pro 14.10.2025 14:30
 *   - Výstup: "2025-10-15T06:00:00+02:00" (následující den v 6:00)
 * 
 * DŮLEŽITÉ: 
 * - Vrací ISO string s explicitním timezone offsetem (např. +01:00, +02:00)
 * - Správně zpracovává DST přechody (letní/zimní čas)
 * - Vytváří moment s konkrétním datem a časem, což zajistí správný DST offset
 * 
 * @param date Datum (může být Date, string nebo timestamp)
 * @returns ISO string s timezone offsetem (např. "2025-10-28T06:00:00+01:00")
 */
export function endOfProductionDayIso(date: Date | string | number): string {
    const dayStartMinutes = getDayStartMinutes();
    const dayStartHours = Math.floor(dayStartMinutes / 60);
    const dayStartMins = dayStartMinutes % 60;
    
    // Vytvoříme moment v Prague timezone (VŽDY Praha, ne lokální TZ prohlížeče!)
    const currentTime = moment.tz(date, DISPLAY_TIMEZONE);
    
    // Vytvoříme moment pro začátek dne (půlnoc) a nastavíme čas
    const currentProdDayStart = currentTime.clone()
        .startOf('day')
        .hour(dayStartHours)
        .minute(dayStartMins)
        .second(0)
        .millisecond(0);
    
    // Pokud jsme před dayStart, výrobní den končí dnes v dayStart čase
    /*if (currentTime.isBefore(currentProdDayStart)) {
        return currentProdDayStart.format();
    }*/
    
    // Jinak výrobní den končí zítra v dayStart čase
    return currentProdDayStart.add(1, 'day').format();
}

/**
 * Získá timezone z aktuálního tenanta.
 * Pokud není definována, vrací undefined a moment použije lokální timezone prohlížeče.
 * 
 * @returns Timezone string (např. "Europe/Prague", "America/New_York") nebo undefined pro lokální TZ
 */
export function getTenantTimeZoneIso(): string | undefined {
    try {
        const tenant: TenantModel = tenantsStore.useGetter(CrudGetter.Detail);
        const timeZone = tenant?.timeZone;
        
        // Pokud není definována timezone nebo je prázdná, použijeme lokální timezone prohlížeče
        if (!timeZone || typeof timeZone !== 'string' || timeZone.trim() === '') {
            return undefined; // undefined = použije se lokální timezone
        }
        
        // Zkontrolujeme, zda je to platná IANA timezone (ne offset jako "+01:00")
        // Pokud začíná na "UTC", "+", "-", je to offset, ne timezone název
        if (timeZone.startsWith('UTC') || timeZone.startsWith('+') || timeZone.startsWith('-')) {
            console.warn(`[getTenantTimeZoneIso] Invalid timezone format: "${timeZone}". Using local timezone instead.`);
            return undefined;
        }
        
        return timeZone;
    } catch {
        return undefined;
    }
}